import {
  ScaffolderFieldError,
  ScaffolderFormHeader,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useState } from 'react';

import { Box, Button, Chip, TextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { SYNC_OPTIONS_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import { SyncOption } from '../types';

const SYNC_OPTIONS = {
  ApplyOnly: { values: ['true', 'false'], default: 'false' },
  ApplyOutOfSyncOnly: { values: ['true', 'false'], default: 'false' },
  Delete: { values: ['true', 'false'], default: 'true' },
  FailOnSharedResource: { values: ['true', 'false'], default: 'false' },
  Force: { values: ['true', 'false'], default: 'false' },
  Prune: { values: ['true', 'false', 'confirm'], default: 'false' },
  PruneLast: { values: ['true', 'false'], default: 'false' },
  PrunePropagationPolicy: {
    values: ['foreground', 'background', 'orphan'],
    default: 'foreground',
  },
  Replace: { values: ['true', 'false'], default: 'false' },
  RespectIgnoreDifferences: { values: ['true', 'false'], default: 'false' },
  ServerSideApply: { values: ['true', 'false'], default: 'false' },
  SkipDryRunOnMissingResource: { values: ['true', 'false'], default: 'false' },
  Validate: { values: ['true', 'false'], default: 'true' },
};

const CUSTOM_VALUE_PREFIX = '__custom__';
const availableSyncOptionKeys = Object.keys(SYNC_OPTIONS);

const getValuesForKey = (key: string): string[] => {
  return SYNC_OPTIONS[key as keyof typeof SYNC_OPTIONS]?.values || [];
};

const getDefaultValueForKey = (key: string): string => {
  const option = SYNC_OPTIONS[key as keyof typeof SYNC_OPTIONS];
  return option?.default || '';
};

const isCustomOption = (option: string): boolean =>
  option.startsWith(CUSTOM_VALUE_PREFIX);

const stripCustomPrefix = (v: string): string =>
  v.replace(CUSTOM_VALUE_PREFIX, '');

const addCustomPrefix = (v: string): string => `${CUSTOM_VALUE_PREFIX}${v}`;

export const SyncOptionsInput = (props: {
  value: SyncOption[];
  onChange: (syncOptions: SyncOption[]) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}) => {
  const classes = useStyles();
  const { value = [], onChange, errors, enableAppSetSupport = false } = props;
  const [inputValues, setInputValues] = useState<Record<number, string>>({});

  const handleAddOption = useCallback(() => {
    onChange([...value, { key: '', value: '' }]);
  }, [value, onChange]);

  const handleRemoveOption = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const handleKeyChange = useCallback(
    (index: number, newKey: string | null) => {
      const updatedOptions = [...value];
      updatedOptions[index] = {
        key: newKey || '',
        value: newKey ? getDefaultValueForKey(newKey) : '',
      };
      onChange(updatedOptions);
    },
    [value, onChange],
  );

  const handleValueChange = useCallback(
    (index: number, newValue: string | null) => {
      const updatedOptions = [...value];
      const cleanValue =
        enableAppSetSupport && isCustomOption(newValue || '')
          ? stripCustomPrefix(newValue || '')
          : newValue || '';
      updatedOptions[index] = { ...updatedOptions[index], value: cleanValue };
      onChange(updatedOptions);
    },
    [value, onChange, enableAppSetSupport],
  );

  const handleValueBlur = useCallback(
    (index: number, currentInputValue: string) => {
      if (
        enableAppSetSupport &&
        currentInputValue &&
        currentInputValue !== value[index]?.value
      ) {
        const updatedOptions = [...value];
        updatedOptions[index] = {
          ...updatedOptions[index],
          value: currentInputValue,
        };
        onChange(updatedOptions);
      }
    },
    [value, onChange, enableAppSetSupport],
  );

  const getOptionsForValue = useCallback(
    (option: SyncOption, index: number): string[] => {
      const baseOptions = option.key ? getValuesForKey(option.key) : [];
      if (!enableAppSetSupport) return baseOptions;

      const currentInputValue = inputValues[index] || '';
      if (!baseOptions.includes(currentInputValue)) {
        return [...baseOptions, addCustomPrefix(currentInputValue)];
      }
      if (option.value && !baseOptions.includes(option.value)) {
        return [...baseOptions, addCustomPrefix(option.value)];
      }
      return baseOptions;
    },
    [enableAppSetSupport, inputValues],
  );

  return (
    <Box>
      <ScaffolderFormHeader
        title="Sync Options"
        description="Customize how ArgoCD syncs the desired state in the target cluster."
        divider
        infoDoc={SYNC_OPTIONS_INFO}
      />
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Sync Options"
          description="Add key-value pairs to specify additional sync options for ArgoCD. These options will be applied when ArgoCD syncs the application in the target cluster."
        />
      </Box>
      {value.map((option, index) => (
        <Box key={index} id={`sync-option-${index}`}>
          <Box className={classes.flexContainer}>
            <Box
              className={`${classes.flexContainer} ${classes.inputContainer} ${classes.keyValuePairContainer}`}
            >
              <Autocomplete
                size="small"
                fullWidth
                value={option.key || null}
                onChange={(_, newValue) => handleKeyChange(index, newValue)}
                options={availableSyncOptionKeys}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Sync Option"
                    variant="outlined"
                    required
                    color="secondary"
                    InputProps={params.InputProps}
                    error={errors.length > 0}
                    fullWidth
                  />
                )}
              />
              <Autocomplete
                size="small"
                fullWidth
                value={option.value || null}
                onChange={(_, newValue) => handleValueChange(index, newValue)}
                options={getOptionsForValue(option, index)}
                disabled={!option.key}
                freeSolo={enableAppSetSupport}
                inputValue={
                  enableAppSetSupport
                    ? inputValues[index] ?? option.value ?? ''
                    : undefined
                }
                onInputChange={
                  enableAppSetSupport
                    ? (_, newInputValue) =>
                        setInputValues(prev => ({
                          ...prev,
                          [index]: newInputValue,
                        }))
                    : undefined
                }
                onBlur={
                  enableAppSetSupport
                    ? () => handleValueBlur(index, inputValues[index] || '')
                    : undefined
                }
                groupBy={
                  enableAppSetSupport
                    ? opt =>
                        isCustomOption(opt)
                          ? 'Templated value'
                          : 'Static Values'
                    : undefined
                }
                filterOptions={
                  enableAppSetSupport
                    ? (options, params) =>
                        options.filter(opt => {
                          if (isCustomOption(opt)) return true;
                          return opt
                            .toLowerCase()
                            .includes(params.inputValue.toLowerCase());
                        })
                    : undefined
                }
                getOptionDisabled={
                  enableAppSetSupport
                    ? opt => opt === CUSTOM_VALUE_PREFIX
                    : undefined
                }
                getOptionLabel={opt =>
                  enableAppSetSupport && isCustomOption(opt)
                    ? stripCustomPrefix(opt)
                    : opt
                }
                renderOption={opt => {
                  if (enableAppSetSupport) {
                    if (opt === CUSTOM_VALUE_PREFIX) {
                      return 'Start typing to use a custom value for ApplicationSet templating';
                    }
                    if (isCustomOption(opt)) {
                      return (
                        <Box display="flex" alignItems="center">
                          <span>Use '{stripCustomPrefix(opt)}'</span>
                        </Box>
                      );
                    }
                  }
                  const isDefault =
                    opt ===
                    (option.key ? getDefaultValueForKey(option.key) : '');
                  return (
                    <Box display="flex" alignItems="center">
                      <span>{opt}</span>
                      {isDefault && (
                        <Chip
                          label="default"
                          size="small"
                          variant="outlined"
                          color="primary"
                          className={classes.defaultOptionChip}
                        />
                      )}
                    </Box>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Value"
                    variant="outlined"
                    required
                    color="secondary"
                    InputProps={params.InputProps}
                    error={errors.length > 0}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box className={classes.removeButtonContainer}>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => handleRemoveOption(index)}
              >
                Remove
              </Button>
            </Box>
          </Box>
        </Box>
      ))}
      <Box className={classes.inputContainer}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddOption}
          size="small"
          color="secondary"
        >
          Add Sync Option
        </Button>
      </Box>
      <Box className={classes.inputContainer}>
        {errors.length > 0 && (
          <Box className={classes.inputContainer}>
            <ScaffolderFieldError message={errors.join(', ')} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
